/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable consistent-return */
import FuseAnimate from '@/@fuse/core/FuseAnimate';
import PageHeader from '@/app/customComponents/PageHeader';
import { uploadFile } from '@/app/services/imageUpload/imageUpload';
import {
	deleteStaffDocument,
	generateStaffCode,
	getCurrentStaffCode,
	getStaffById,
	updateStaff,
} from '@/app/services/staff/staff';
import * as Actions from '@/app/store/actions';
import { getImageUrl, isImageFile } from '@/utils/utils';
import { Avatar, CircularProgress, Tooltip, Typography } from '@material-ui/core';
import GradientButton from '@shared/components/GradientButton';
import StyledInput from '@shared/components/StyledInput';
import StyledSelect from '@shared/components/StyledSelect';
import CloudUploadGradient from '@shared/icons/CloudUploadGradient';
import EditPhotoGradientIcon from '@shared/icons/EditPhotoGradientIcon';
import UploadPhotoGradientIcon from '@shared/icons/UploadPhotoGradientIcon';
import { useEffect, useRef, useState } from 'react';
import Dropzone from 'react-dropzone';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import './staff.css';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import RegenerateIcon from '@shared/icons/RegenerateIcon';
import { useNavigate } from 'react-router-dom';

const EditStaff = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { id } = useParams();
	const [isLoading, setIsLoading] = useState(false);
	const [selectedFile, setSelectedFile] = useState(null);
	const [preview, setPreview] = useState(null);
	const [attachFile, setAttachFile] = useState([]);
	const [documentsToDelete, setDocumentsToDelete] = useState([]);
	const inputRef = useRef(null);
	const [originalData, setOriginalData] = useState(null);
	const [showCode, setShowCode] = useState(false);
	const [staffCode, setStaffCode] = useState('');
	const [staffCodeLoading, setStaffCodeLoading] = useState(false);

	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm({
		defaultValues: {
			first_name: '',
			last_name: '',
			email: '',
			phone: '',
			emergency_phone: '',
			emergency_name: '',
			admin: 'yes',
			title: 'full-time',
			position_type: 'admin',
			date_of_birth: null,
			address: '',
			employment_date: null,
		},
	});

	useEffect(() => {
		const fetchStaffData = async () => {
			try {
				const response = await getStaffById(id);
				const staffData = response.data;
				setOriginalData(staffData);
				setPreview(staffData.thumb);
				setAttachFile(staffData.document_urls || []);
				reset({
					first_name: staffData.first_name,
					last_name: staffData.last_name,
					email: staffData.email,
					phone: staffData.phone || '',
					emergency_phone: staffData.emergency_phone || '',
					emergency_name: staffData.emergency_name || '',
					admin: staffData.admin,
					title: staffData.title,
					position_type: staffData.position_type,
					date_of_birth: staffData.date_of_birth,
					address: staffData.address,
					employment_date: staffData.employment_date,
				});
			} catch (error) {
				dispatch(
					Actions.showMessage({
						message: 'Failed to fetch staff information.',
						variant: 'error',
					})
				);
			}
		};
		fetchStaffData();
	}, [id, reset, dispatch]);

	useEffect(() => {
		if (!selectedFile) return;
		const objectUrl = URL.createObjectURL(selectedFile);
		setPreview(objectUrl);
		return () => URL.revokeObjectURL(objectUrl);
	}, [selectedFile]);

	const onSelectFile = (e) => {
		if (!e.target.files || e.target.files.length === 0) {
			setSelectedFile(null);
			return;
		}
		const file = e.target.files[0];
		if (!isImageFile(file)) {
			dispatch(Actions.showMessage({ message: 'Please select an image file', variant: 'error' }));
		} else {
			setSelectedFile(file);
		}
	};

	const removeItem = (index) => {
		const fileToRemove = attachFile[index];
		if (fileToRemove.id) {
			setDocumentsToDelete((prev) => [...prev, fileToRemove.id]);
		}
		setAttachFile(attachFile.filter((_, i) => i !== index));
	};

	const onSubmit = async (data) => {
		setIsLoading(true);
		try {
			const formData = { ...data };

			if (formData.date_of_birth && formData.date_of_birth !== originalData.date_of_birth) {
				formData.date_of_birth = formData.date_of_birth.format('YYYY-MM-DD');
			} else {
				formData.date_of_birth = originalData.date_of_birth;
			}

			if (formData.employment_date && formData.employment_date !== originalData.employment_date) {
				formData.employment_date = formData.employment_date.format('YYYY-MM-DD');
			} else {
				formData.employment_date = originalData.employment_date;
			}

			if (selectedFile) {
				const filename = getImageUrl(selectedFile);
				let url = `${import.meta.env.VITE_S3_BASE_URL}`;
				url += await uploadFile(selectedFile, filename);
				formData.photo = url;
				formData.thumb = url;
			} else {
				formData.photo = originalData.photo;
			}

			if (attachFile.length > 0 || documentsToDelete.length > 0) {
				const deletePromises = documentsToDelete.map((docId) => deleteStaffDocument(id, docId));
				await Promise.all(deletePromises);

				const newFiles = attachFile.filter((file) => !file.url);
				if (newFiles.length > 0) {
					const upload_files = newFiles.map((e) => uploadFile(e, getImageUrl(e)));
					const uploaded = await Promise.all(upload_files);
					formData.document_urls = uploaded.map((url, i) => ({
						url: `${import.meta.env.VITE_S3_BASE_URL}${url}`,
						name: newFiles[i].name,
					}));
				} else {
					formData.document_urls = [];
				}
			} else {
				formData.document_urls = [];
			}

			const finalFormData = {
				title: formData.title,
				first_name: formData.first_name,
				last_name: formData.last_name,
				email: formData.email,
				phone: formData.phone || '',
				emergency_name: formData.emergency_name || '',
				emergency_phone: formData.emergency_phone || '',
				photo: formData.photo,
				position_type: formData.position_type,
				admin: formData.admin,
				date_of_birth: formData.date_of_birth,
				address: formData.address || '',
				employment_date: formData.employment_date,
				document_urls: formData.document_urls,
				status: originalData.status,
			};

			await updateStaff(finalFormData, id);
			dispatch(Actions.showMessage({ message: 'Staff updated successfully', variant: 'success' }));
			navigate(-1);
		} catch (error) {
			dispatch(Actions.showMessage({ message: error.message || 'Error updating staff', variant: 'error' }));
		} finally {
			setIsLoading(false);
		}
	};

	const handleGetStaffCode = async () => {
		setStaffCodeLoading(true);
		try {
			const response = await getCurrentStaffCode(id);
			setStaffCode(response.data.code);
		} catch (error) {
			dispatch(Actions.showMessage({ message: error.message || 'Error getting staff code', variant: 'error' }));
		} finally {
			setStaffCodeLoading(false);
		}
	};

	const handleResetStaffCode = async () => {
		setStaffCodeLoading(true);
		try {
			const response = await generateStaffCode(id);
			setStaffCode(response.data.code);
			dispatch(Actions.showMessage({ message: 'Staff code reset successfully', variant: 'success' }));
		} catch (error) {
			dispatch(Actions.showMessage({ message: error.message || 'Error resetting staff code', variant: 'error' }));
		} finally {
			setStaffCodeLoading(false);
		}
	};

	return (
		<FuseAnimate animation="transition.slideLeftIn" duration={600}>
			<div className="mb-[162px] mx-60">
				<div className="flex justify-between items-end my-[40px]">
					<PageHeader onBack={() => navigate(-1)} title="Edit Staff" />
				</div>
				<div className="bg-white rounded-3xl p-60 shadow-md">
					<div className="font-bold">
						<h1 className="text-[18px]">Staff Information</h1>
					</div>
					<div className="mt-32 flex flex-col md:flex-row gap-32 md:gap-60 items-start">
						<div className="mx-auto md:mx-0">
							<button
								className="flex items-center gap-8 flex-col relative justify-self-center group"
								onClick={() => inputRef.current.click()}
								type="button"
							>
								{preview ? (
									<>
										<Avatar className="w-[120px] h-[120px]" src={preview} />
										<EditPhotoGradientIcon className="absolute bottom-0 right-0 w-32 h-32" />
									</>
								) : (
									<>
										<div className="w-[120px] h-[120px] grid grid-cols-8 rounded-full overflow-hidden group-hover:opacity-70 transition-all duration-300 group-active:shadow-md">
											{Array.from({ length: 64 }).map((_, index) => (
												<div
													className={`w-full h-full ${
														Math.floor(index / 8) % 2 === index % 2 ? 'bg-gray-300' : 'bg-gray-200'
													}`}
													key={index}
												/>
											))}
										</div>
										<UploadPhotoGradientIcon className="absolute bottom-0 right-0 w-32 h-32" />
									</>
								)}
							</button>
							<input accept="image/*" onChange={onSelectFile} ref={inputRef} style={{ display: 'none' }} type="file" />
						</div>

						<div className="flex flex-col gap-36 w-full mt-32">
							<div
								className="grid gap-40 gap-y-60"
								style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}
							>
								<Controller
									control={control}
									name="first_name"
									render={({ field }) => (
										<StyledInput
											{...field}
											error={!!errors.first_name}
											helperText={errors.first_name?.message}
											label="First Name"
											placeholder="Enter first name"
										/>
									)}
									rules={{ required: 'First name is required' }}
								/>
								<Controller
									control={control}
									name="last_name"
									render={({ field }) => (
										<StyledInput
											{...field}
											error={!!errors.last_name}
											helperText={errors.last_name?.message}
											label="Last Name"
											placeholder="Enter last name"
										/>
									)}
									rules={{ required: 'Last name is required' }}
								/>
								<Controller
									control={control}
									name="email"
									render={({ field }) => (
										<StyledInput
											{...field}
											error={!!errors.email}
											helperText={errors.email?.message}
											label="Email"
											placeholder="Enter email"
											type="email"
										/>
									)}
									rules={{
										required: 'Email is required',
										pattern: {
											value: /^\S+@\S+\.\S+$/,
											message: 'Please enter a valid email',
										},
									}}
								/>
								<Controller
									control={control}
									name="phone"
									render={({ field }) => (
										<StyledInput
											{...field}
											error={!!errors.phone}
											helperText={errors.phone?.message}
											label="Phone"
											placeholder="Enter phone"
											type="phone"
										/>
									)}
									rules={{
										validate: {
											length: (value) =>
												!value || // Allow empty, but validate if not
												value.replace(/\D/g, '').length >= 10 ||
												'Please enter a valid phone number',
										},
									}}
								/>
								<Controller
									control={control}
									name="emergency_name"
									render={({ field }) => (
										<StyledInput
											{...field}
											error={!!errors.emergency_name}
											helperText={errors.emergency_name?.message}
											label="Emergency Contact Name"
											placeholder="Enter emergency contact name"
										/>
									)}
								/>
								<Controller
									control={control}
									name="emergency_phone"
									render={({ field }) => (
										<StyledInput
											{...field}
											error={!!errors.emergency_phone}
											helperText={errors.emergency_phone?.message}
											label="Emergency Phone"
											placeholder="Enter emergency phone"
											type="phone"
										/>
									)}
									rules={{
										validate: {
											length: (value) =>
												!value || // Allow empty, but validate if not
												value.replace(/\D/g, '').length >= 10 ||
												'Please enter a valid phone number',
										},
									}}
								/>
								<Controller
									control={control}
									name="position_type"
									render={({ field }) => (
										<StyledSelect
											{...field}
											error={!!errors.position_type}
											helperText={errors.position_type?.message}
											label="Position"
											options={[
												{ value: 'lead', label: 'Lead' },
												{ value: 'assistant', label: 'Assistant' },
												{ value: 'admin', label: 'Admin' },
												{ value: 'cook', label: 'Cook' },
											]}
											placeholder="Select position"
										/>
									)}
								/>
								<Controller
									control={control}
									name="title"
									render={({ field }) => (
										<StyledSelect
											{...field}
											error={!!errors.title}
											helperText={errors.title?.message}
											label="Title"
											options={[
												{ value: 'full-time', label: 'Full-time' },
												{ value: 'part-time', label: 'Part-time' },
											]}
											placeholder="Select hours"
										/>
									)}
								/>
								<Controller
									control={control}
									name="admin"
									render={({ field }) => (
										<StyledSelect
											{...field}
											error={!!errors.admin}
											helperText={errors.admin?.message}
											label="Admin"
											options={[
												{ value: 'yes', label: 'Yes' },
												{ value: 'no', label: 'No' },
											]}
											placeholder="Select admin status"
										/>
									)}
								/>
								<Controller
									control={control}
									name="date_of_birth"
									render={({ field }) => {
										const { value } = field;
										return (
											<StyledInput
												{...field}
												error={!!errors.date_of_birth}
												helperText={errors.date_of_birth?.message}
												label="Date of Birth"
												placeholder="YYYY-MM-DD"
												type="date"
												value={value}
											/>
										);
									}}
								/>
								<Controller
									control={control}
									name="address"
									render={({ field }) => <StyledInput {...field} label="Address" placeholder="Enter address" />}
								/>
								<Controller
									control={control}
									name="employment_date"
									render={({ field }) => (
										<StyledInput
											{...field}
											error={!!errors.employment_date}
											helperText={errors.employment_date?.message}
											label="Employment Date"
											placeholder="YYYY-MM-DD"
											type="date"
										/>
									)}
								/>
								<div className="flex items-center gap-4">
									<StyledInput
										disabled
										icon={
											<Tooltip title={`${showCode ? 'Hide code' : 'Show code'}`}>
												<div className="flex items-center gap-10">
													<button
														onClick={() => {
															if (!showCode) {
																setShowCode(true);
																handleGetStaffCode();
															} else {
																setShowCode(false);
																setStaffCode('');
															}
														}}
														type="button"
													>
														{!showCode ? <EyeIcon className="size-20" /> : <EyeSlashIcon className="size-20" />}
													</button>
												</div>
											</Tooltip>
										}
										label="Login Code"
										placeholder="****"
										type={showCode ? 'text' : 'password'}
										value={staffCodeLoading ? 'loading...' : staffCode || ''}
									/>
									<Tooltip title="Generate new code">
										<button
											className="shadow-skeumorphic h-full px-12 rounded-[10px] hover:shadow-skeumorphic-hover active:shadow-skeumorphic-active"
											onClick={handleResetStaffCode}
											type="button"
										>
											<RegenerateIcon className="size-20" />
										</button>
									</Tooltip>
								</div>

								<div className="col-span-full">
									<Typography className="mb-16 text-[18px] font-bold">Documents</Typography>
									<Controller
										control={control}
										name="attachments"
										render={() => (
											<>
												<Dropzone
													accept={['image/jpeg', 'image/png', 'application/pdf']}
													maxSize={2097152}
													multiple
													onDrop={(acceptedFiles, rejectedFiles) => {
														if (rejectedFiles.length > 0) {
															rejectedFiles.forEach(({ file, errors }) => {
																if (errors[0]?.code === 'file-too-large') {
																	dispatch(
																		Actions.showMessage({
																			message: `${file.name} is too large. Max size is 2MB`,
																			variant: 'error',
																		})
																	);
																} else {
																	dispatch(
																		Actions.showMessage({
																			message: `${file.name} is not a valid file type`,
																			variant: 'error',
																		})
																	);
																}
															});
														}
														setAttachFile((prev) => [...prev, ...acceptedFiles]);
													}}
												>
													{({ getRootProps, getInputProps }) => (
														<div
															{...getRootProps()}
															className="rounded p-36 md:p-24 text-center cursor-pointer flex flex-col items-center justify-center gap-12"
															style={{
																border: '1px solid transparent',
																borderRadius: '1rem',
																borderImage:
																	'repeating-linear-gradient(45deg, #80bfff, #80bfff 4px, transparent 4px, transparent 8px) 1 round',
															}}
														>
															<input {...getInputProps()} />
															<CloudUploadGradient className="w-[60px]" />
															<div className="content flex-col items-center gap-12 hidden md:flex">
																<span>Drag & drop files here.</span>
																<div className="flex items-center gap-8">
																	<div className="h-px w-64 bg-gray-200" />
																	<span className="text-12 text-gray-600">OR</span>
																	<div className="h-px w-64 bg-gray-200" />
																</div>
															</div>
															<GradientButton variant="light-blue">Choose files</GradientButton>
														</div>
													)}
												</Dropzone>

												{attachFile?.length > 0 ? (
													<div className="grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-16 mt-24">
														{attachFile.map((file, i) => (
															<Tooltip key={i} title={file.name || file.file_name}>
																<div className="relative group">
																	<div className="aspect-square rounded-lg overflow-hidden border border-gray-200">
																		{file.type?.startsWith('image/') || file.url?.match(/\.(jpg|jpeg|png|gif)$/i) ? (
																			<img
																				alt={file.name || file.file_name}
																				className="w-full h-full object-cover"
																				src={file.url || URL.createObjectURL(file)}
																			/>
																		) : (
																			<div className="w-full h-full flex items-center justify-center bg-gray-50">
																				<img alt="PDF" className="w-64" src="assets/images/pdf_thumbnail.png" />
																			</div>
																		)}
																	</div>
																	<button
																		className="absolute -top-2 -right-2 size-24 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
																		onClick={(e) => {
																			e.stopPropagation();
																			removeItem(i);
																		}}
																		type="button"
																	>
																		×
																	</button>
																	<div className="mt-2 text-center text-sm text-gray-600 truncate">
																		{file.name || file.file_name}
																	</div>
																</div>
															</Tooltip>
														))}
													</div>
												) : null}
											</>
										)}
									/>
								</div>
							</div>
						</div>
					</div>
					<div className="flex justify-center md:justify-end mt-40 gap-32">
						{isLoading ? (
							<CircularProgress size={35} />
						) : (
							<>
								<GradientButton
									id="cancel"
									onClick={() => navigate(-1)}
									type="button"
									variant="secondary"
									width="140px"
								>
									Cancel
								</GradientButton>
								<GradientButton
									id="submit"
									onClick={handleSubmit(onSubmit)}
									type="submit"
									variant="blurple"
									width="140px"
								>
									Update
								</GradientButton>
							</>
						)}
					</div>
				</div>
			</div>
		</FuseAnimate>
	);
};

export default EditStaff;
